<template>
  <div v-if="!loading" class="full-width full-height overflow-y-auto light-grey-bg pa-4">
    <v-row no-gutters class="full-width text-center" style="border: 1px solid black; border-radius: 5px;" justify="center">
      Sélection des clients
    </v-row>
    <div class="full-width text-center d-flex justify-end py-2">
      <v-btn outlined class="third white--text" @click="openSentEmailModal">
          <v-icon>mdi-text-box-search-outline</v-icon>
          Historique des envois
        </v-btn>
    </div>
    <v-row>
      <v-col cols="4">
        <v-card>
          <v-card-text>
            <v-checkbox
              v-model="haveDigitalAccount"
              label="Compte digital"
              color="third"
              hide-details
            />
            <v-checkbox
              v-model="haveLicence"
              label="Licences"
              color="third"
              hide-details
            />
            <v-checkbox
              v-model="haveInsurance"
              label="Assurance annuel"
              color="third"
              hide-details
            />
            <v-checkbox
              v-model="showParticipantFromInstructorSessions"
              label="Les clients sont dans mes sessions "
              color="third"
              hide-details
            />
            <v-range-slider
              v-model="ageRange"
              :max="maxAge"
              :min="minAge"
              hide-details
              thumb-label
              thumb-color="third"
              track-color="third"
              track-fill-color="third"
              class="pt-4 align-center"
            >
              <template #prepend>
                <span>Age:</span>
              </template>
            </v-range-slider>
            <v-text-field
              v-model="search"
              color="third"
              flat
              hide-details
              clearable
              append-icon="mdi-magnify"
              class="pt-3"
              label="Recherche"
            />
            <div class="mt-4" align="center">
              <v-btn v-if="canUserPerformAction" color="third" class="white--text" height="32" @click="searchOnAlgolia">
                Rechercher
              </v-btn>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="8">
        <v-card>
          <v-card-text>
            <v-text-field
              v-model="searchSessions"
              color="third"
              append-icon="mdi-magnify"
              label="Nom de la session"
              single-line
              hide-details
              class="pb-4 pt-0 mt-0"
              @click.stop
            />
            <v-data-table
              v-model="selectedSession"
              :search="searchSessions"
              :items="sessionsData"
              :items-per-page="-1"
              :hide-default-footer="true"
              :headers="headers"
              item-key="uuid"
              item-text="title"
              color="thrid"
              height="273"
              show-select
              checkbox-color="third"
              @toggle-select-all="selectAllSessions"
              @click:row="rowClick"
              @item-selected="selectItem"
            />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <ExpandableContentTables class="full-width" :headers="customerHeaders" :items="noSelected" :items-selected="selectedAssignCustomer" @updateSelected="updateSelected" />
      </v-col>
    </v-row>
    <v-row class="py-4" align="center">
      <v-col>
        <v-card width="100%">
          <v-card-text class="d-flex">
            <SelectPicker
              v-model="selectedTemplate"
              class="pr-4"
              subtitle="Sélectionnez Une email template"
              :items="emailTemplates"
              :return-object="true"
              :select-item="itemKeyValues"
              @selectedInput="selectedEmailTemplate"
            />
            <div class="d-flex align-center">
              <v-btn v-if="canUserPerformAction" color="third" class="white--text" height="52" :disabled="!selectedTemplate || !selectedAssignCustomer.length" @click="sendEmailToCustomers">
                Envoyer un e-mail aux clients sélectionnés
              </v-btn>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <SentEmailModal v-if="showSentEmailModal" :show="showSentEmailModal" :email-templates="emailTemplates" @refreshEmails="getEmails" @closeModal="closeSentEmailModal" />
  </div>
  <div v-else class="d-flex justify-center align-center full-height">
    <Loader />
  </div>
</template>

<script>
import { ref, computed, onBeforeMount } from '@vue/composition-api';
import { format, fromUnixTime, differenceInYears } from 'date-fns';
import { fr } from 'date-fns/esm/locale';
import Loader from '@/components/common/Loader.vue';
import ExpandableContentTables from '@/components/email/ExpandableContentTables.vue';
import { sendNotification } from '@/helpers/utils';
import { getEmailTemplate, sendEmail, getClientsReceivedEmails, getCustomersByUUids } from '@/helpers/request';
import { searchCustomerOnAlgolia, searchCustomer } from '@/helpers/search';
import SentEmailModal from '@/components/email/SentEmailModal.vue';
import SelectPicker from '@/components/common/Form/SelectPicker.vue';

import store from '@/store';

export default {
  components: { Loader, ExpandableContentTables, SelectPicker, SentEmailModal },
  setup () {
    const loading = ref(false);
    const ageRange = ref([3, 99]);
    const minAge = ref(3);
    const maxAge = ref(99);
    const search = ref('');
    const searchSessions = ref('');
    const selectedSession = ref([]);

    const emailTemplates = ref([]);
    const selectedTemplate = ref(null);
    const customerHeadersChanged = ref(false);
    const noSelectedWithEmail = ref([]);

    const haveDigitalAccount = ref(false);
    const haveLicence = ref(false);
    const haveInsurance = ref(false);
    const showParticipantFromInstructorSessions = ref(false);

    const itemKeyValues = ref({
      value: 'uuid',
      label: 'name'
    });

    const headers = [
      { text: 'Nom', value: 'title', width: '40%' },
      { text: 'Date de la session', value: 'date', align: 'center', width: '30%' },
      { text: 'Heure de début', value: 'startTime', align: 'center', width: '15%' },
      { text: 'Heure de fin', value: 'endTime', align: 'center', width: '15%' }
    ];

    const customerHeaders = computed(() => {
      const headersChanges = [
        { text: 'Nom', value: 'lastname' },
        { text: 'Prénom', value: 'firstname' },
        { text: 'Age', value: 'age' },
        { text: 'Espace digital', value: 'digitalAccountLabel' },
        { text: 'Email', value: 'email' }
      ];
      if (customerHeadersChanged.value) {
        headersChanges.push({ text: 'Email déjà envoyé', value: 'receivedEmail' });
      }
      return headersChanges;
    });

    const weeklySessions = computed(() => store.state.sessions);

    const sessionsData = computed(() => (
      weeklySessions.value.map(s => ({
        id: s.id,
        uuid: s.uuid,
        title: s.name,
        date: format(fromUnixTime(s.start_time), 'dd-MM-yyyy (EEEE)', { locale: fr }),
        startTime: format(fromUnixTime(s.start_time), 'HH:mm'),
        endTime: format(fromUnixTime(s.end_time), 'HH:mm')
      }))
    ));

    const selectedAssignCustomer = ref([]);
    const sessionParticipants = ref([]);

    const noSelected = computed(() => sessionParticipants.value.filter(sp => !sp.isGuest));

    const selectedAndNoSelectedCustomers = computed(() => [...noSelected.value, ...selectedAssignCustomer.value]);

    const canUserPerformAction = computed(() => store.getters.canUserPerformAction);

    const showSentEmailModal = ref(false);

    const openSentEmailModal = () => {
      showSentEmailModal.value = true;
    };

    const closeSentEmailModal = () => {
      showSentEmailModal.value = false;
    };

    const updateSelected = items => {
      const { selected, deselected } = items;

      deselected.map(item => {
        const indexNonSelected = sessionParticipants.value.findIndex(c => c.uuid === item.uuid);
        if (indexNonSelected !== -1) {
          sessionParticipants.value.splice(indexNonSelected, 1);
        }
        selectedAssignCustomer.value.push(item);
      });

      selected.map(item => {
        const indexSelectedAssign = selectedAssignCustomer.value.findIndex(c => c.uuid === item.uuid);
        if (indexSelectedAssign !== -1) {
          selectedAssignCustomer.value.splice(indexSelectedAssign, 1);
          sessionParticipants.value.push(item);
        }
      });
    };

    const searchOnAlgolia = async () => {
      if (!store.getters.canUserPerformAction) {
        return;
      }

      await searchCustomerOnAlgolia(search.value);
      if (showParticipantFromInstructorSessions.value) {
        store.state.sessions.forEach(s => {
          const foundInstructor = s.instructors.find(instructor => instructor.uuid === store.state.user.uuid);
          if (foundInstructor) {
            (s.participants || []).forEach(p => {
              const isParticipantOnArray = store.state.algoliaCustomers.some(c => c.uuid === p.uuid);
              if (!isParticipantOnArray) {
                store.commit('setAlgoliaCustomer', { uuid: p.uuid, horanet_id: p.horanet_id, firstname: p.firstname, lastname: p.lastname, age: '--', fullname: `${p.firstname} ${p.lastname}` });
              }
            });
          }
        });
      }
    };

    const getSessionParticipantsDetail = async items => {
      const uuids = [];
      items.map(item => {
        const sessionDetails = store.getters.session(item.uuid);
        (sessionDetails.participants || []).filter(p => !p.isGuest && !selectedAndNoSelectedCustomers.value.find(v => v.uuid === p.uuid)).map(participant => uuids.push(participant.uuid));
      });

      if (uuids.length) {
        return getCustomersByUUids([...new Set(uuids)]);
      }

      return [];
    };

    const selectItem = async ({ item, value, selectAll, response = null }) => {
      const sessionDetails = store.getters.session(item.uuid);
      if (sessionDetails.participants.length < 1) {
        return;
      }

      let auxiliarResponse = { ...response };

      if (!selectAll && value) {
        auxiliarResponse = await getSessionParticipantsDetail([item]);
      }

      sessionDetails.participants.map(s => {
        if (value) {
          if (!selectedAndNoSelectedCustomers.value.some(v => v.uuid === s.uuid)) {
            let customer;
            let digitalAccount = '--';
            if (auxiliarResponse && auxiliarResponse.success) {
              customer = auxiliarResponse.data.items.find(c => c.uuid === s.uuid);

              if (customer) {
                digitalAccount = customer.digital_account ? 'Oui' : 'Non';
              }
            }

            sessionParticipants.value.push({
              ...s,
              digitalAccountLabel: digitalAccount,
              age: customer ? differenceInYears(new Date(), new Date(`${customer.birth_date}`)) : '-',
              email: customer ? customer.email : null
            });
          }
        } else {
          const indexParticipant = sessionParticipants.value.findIndex(v => v.uuid === s.uuid);
          if (indexParticipant !== -1) {
            sessionParticipants.value.splice(indexParticipant, 1);
          }
        }
      });
    };

    const selectAllSessions = async ({ items, value }) => {
      const response = await getSessionParticipantsDetail(items);
      items.map(item => {
        selectItem({ item, value, selectAll: true, response });
      });
    };

    const rowClick = (item, row, event) => {
      event.stopPropagation();
      row.select(!row.isSelected);
    };

    const emailRecipients = (customer, recipients = []) => {
      if (!recipients.some(c => c.uuid === customer.uuid)) {
        recipients.push({
          uuid: customer.uuid,
          email: customer.email
        });
      }
    };

    const getCustomersEmail = async () => {
      loading.value = true;
      const customersToSearch = selectedAssignCustomer.value.filter(c => !c.email);
      const recipients = [];
      const uuids = customersToSearch.map(c => c.uuid).join(' OR uuid: ');
      const filter = `uuid: ${uuids}`;
      if (customersToSearch.length) {
        const { hits } = await searchCustomer('', filter, 0, customersToSearch.length);
        hits.map(c => {
          emailRecipients(c, recipients);
        });
      }

      selectedAssignCustomer.value = selectedAssignCustomer.value.map(c => {
        if (c.email) {
          return c;
        }

        const info = recipients.find(cust => cust.uuid === c.uuid);

        if (info) {
          c.email = info.email;
        }

        return c;
      });
      loading.value = false;
    };

    const sendEmailToCustomers = async () => {
      const customersToSearch = [];
      const recipients = [];
      selectedAssignCustomer.value.map(customer => {
        if (!customer.email) {
          customersToSearch.push(customer);
        }
        emailRecipients(customer, recipients);
      });
      const uuids = customersToSearch.map(c => c.uuid).join(' OR uuid: ');
      const filter = `uuid: ${uuids}`;
      if (customersToSearch.length) {
        const { hits } = await searchCustomer('', filter, 0, customersToSearch.length);
        hits.map(c => {
          emailRecipients(c, recipients);
        });
      }

      const validRecipients = recipients.filter(r => r.email);

      if(!validRecipients.length) {
        sendNotification('error', 'Aucun email valide trouvé');
        return;
      }

      loading.value = true;
      const response = await sendEmail({ emailUuid: selectedTemplate.value.uuid, recipients: validRecipients, workspace: store.state.workspace.code });
      loading.value = false;

      sendNotification(response.success ? 'success' : 'error', response.success ? 'email envoyé avec succès' : 'erreur lors de l\'envoi du mail');
    };

    const getEmails = async () => {
      loading.value = true;
      const response = await getEmailTemplate();

      emailTemplates.value = response.data;
      loading.value = false;
    };

    onBeforeMount(async () => {
      await getEmails();
    });

    const selectedEmailTemplate = async value => {
      noSelectedWithEmail.value = [];
      const clientsReceivedThisEmail = await getClientsReceivedEmails(value.uuid);
      noSelectedWithEmail.value = clientsReceivedThisEmail.data;

      selectedAssignCustomer.value = selectedAssignCustomer.value.map(customer => {
        const receivedEmail = clientsReceivedThisEmail.data.find(c => c.customerUuid === customer.uuid);
        return { ...customer, receivedEmail: receivedEmail ? 'Oui' : 'Non' };
      });
      customerHeadersChanged.value = true;
    };

    return {
      getCustomersEmail,
      selectAllSessions,
      selectedEmailTemplate,
      itemKeyValues,
      sendEmailToCustomers,
      selectedTemplate,
      emailTemplates,
      selectItem,
      searchOnAlgolia,
      updateSelected,
      customerHeaders,
      noSelected,
      selectedAssignCustomer,
      sessionsData,
      selectedSession,
      rowClick,
      searchSessions,
      headers,
      canUserPerformAction,
      loading,
      haveDigitalAccount,
      haveLicence,
      haveInsurance,
      showParticipantFromInstructorSessions,
      ageRange,
      minAge,
      maxAge,
      search,
      weeklySessions,
      showSentEmailModal,
      openSentEmailModal,
      closeSentEmailModal,
      getEmails
    };
  }
};
</script>
